import { inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';

/**
 * Selects a slice of data from an {@link Store NGXS store}.
 * Uses {@link Store#select} internally and converts its observable to a signal.
 *
 * @example
 * ```ts
 * @Component({
 *    template: `
 *      My books:
 *      <ul>
 *        <li *ngFor="let book of books()>{{ book }}</li>
 *      </ul>
 *     `
 * })
 * class BooksComponent {
 *
 *    // legacy way using an Observable
 *    @Select(BooksState.books)
 *    books$: Observable<string[]>;
 *
 *    // new way using a signal
 *    books = useSelect(BooksState.books);
 * }
 * ```
 *
 * @param selector The selector function or key
 * @returns A signal holding the selected slice of state
 */
function useSelect(selector) {
    const store = inject(Store);
    return toSignal(store.select(selector), { initialValue: store.selectSnapshot(selector) });
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * @packageDocumentation
 * Composables for [NGXS](https://www.ngxs.io/).
 *
 * @example
 * ```ts
 * @Component({
 *    template: `
 *      My books:
 *      <ul>
 *        <li *ngFor="let book of books()>{{ book }}</li>
 *      </ul>
 *     `
 * })
 * class BooksComponent {
 *
 *    // legacy way using an Observable
 *    @Select(BooksState.books)
 *    books$: Observable<string[]>;
 *
 *    // new way using a signal
 *    books = useSelect(BooksState.books);
 * }
 * ```
 *
 * @module @bynary/composables/ngxs
 */

/**
 * Generated bundle index. Do not edit.
 */

export { useSelect };
